'use client'
import { useApi } from '@repo/api'
import { DEFAULT_PAGE_SIZE } from '@repo/sdk/constants'
import { format } from '@repo/sdk/date'
import { useParseSearchParams } from '@repo/sdk/qs'
import { useSuspenseQuery } from '@repo/tanstack/query'
import { useTableSortParams } from '@repo/table'
import {
  type FilterOperators,
  resolveDateFilterValues,
  useDataTableFiltersQuery,
} from '@repo/table/filters'

import { COMPANY_TAGS_FILTER_ID, useCompanyTagAccess } from './use-company-tags'

import type { CompanyListFilter } from '../_utils/company-type'

export type { CompanyListFilter }

/** A row of any company list. */
export type CompanyListRow = ReturnType<typeof useSuspenseFetchCompanies>['data']['data'][number]

// The first page of a Company list with no filters, search or sort: newest first.
export const DEFAULT_COMPANIES_LIST_INPUT = {
  page: 1,
  perPage: DEFAULT_PAGE_SIZE,
  'sort[created_at]': 'desc',
}

// The Company list request the URL and the table's sort ask for.
export const useCompaniesListInput = () => {
  const { parsedParams } = useParseSearchParams()
  const filterParams = useBuildFilterSearchParams()
  const sortParams = useTableSortParams()

  // Extract non-sort params to avoid conflicts
  const { filters: _filters, sort: _sort, ...otherParams } = parsedParams

  const searchParams: Record<string, number | string> = {
    ...filterParams,
    ...otherParams,
    page: Number(parsedParams.page ?? DEFAULT_COMPANIES_LIST_INPUT.page),
    perPage: Number(parsedParams.perPage ?? DEFAULT_COMPANIES_LIST_INPUT.perPage),
  }

  const hasSort = Object.keys(sortParams).length > 0
  if (hasSort) {
    Object.assign(searchParams, sortParams)
  } else {
    searchParams['sort[created_at]'] = DEFAULT_COMPANIES_LIST_INPUT['sort[created_at]']
  }

  return searchParams
}

export const useSuspenseFetchCompanies = (filter: CompanyListFilter) => {
  const api = useApi()
  const input = useCompaniesListInput()

  return useSuspenseQuery(api.companies.listByType.queryOptions({ input, path: { filter } }))
}

type RangeOperator = FilterOperators['date'] | FilterOperators['number']

const COMPARISON_PREFIXES = new Map<RangeOperator, string>([
  ['is after', '>'],
  ['is before', '<'],
  ['is greater than', '>'],
  ['is greater than or equal to', '>='],
  ['is less than', '<'],
  ['is less than or equal to', '<='],
  ['is on or after', '>='],
  ['is on or before', '<='],
])

/**
 * A number or date comparison as the API's range filters take it: one value, `from,to`, or a
 * comparison prefix. The API has no negation, so "is not" and "is not between" are left out
 * rather than sent as their opposite.
 */
const rangeParam = (operator: RangeOperator, values: readonly (number | string)[]) => {
  const [first] = values
  if (first === undefined) return undefined
  if (operator === 'is') return String(first)
  if (operator === 'is between') return values.length === 2 ? values.join(',') : undefined
  const prefix = COMPARISON_PREFIXES.get(operator)
  return prefix ? `${prefix}${first}` : undefined
}

const useBuildFilterSearchParams = () => {
  const { filters } = useDataTableFiltersQuery()
  const { canView: canViewTags } = useCompanyTagAccess()

  const params: Record<string, number | string | undefined> = {}

  filters.forEach(filter => {
    // The API answers the Company tags filter with 403 for users who may not see Company tags, so a shared
    // URL that has it still loads their list.
    if (filter.columnId === COMPANY_TAGS_FILTER_ID) {
      // The API filters by tags only to include them (OR), so an exclude from a crafted URL is
      // left out rather than sent as its opposite.
      if (!canViewTags || filter.operator.startsWith('exclude')) return
    }

    switch (filter.type) {
      case 'date': {
        const days = resolveDateFilterValues(filter.values).map(value =>
          format(value, 'yyyy-MM-dd'),
        )
        const range = rangeParam(filter.operator, days)
        if (range !== undefined) params[`filter[${filter.columnId}]`] = range
        break
      }
      case 'multiOption': {
        params[`filter[${filter.columnId}]`] = filter.values.join(',')
        break
      }
      case 'number': {
        // Credit limits go to the API in minor units: 19.99 * 100 is 1998.9999999999998, so round.
        const values =
          filter.columnId === 'credit_limit'
            ? filter.values.map(value => Math.round(value * 100))
            : filter.values
        const range = rangeParam(filter.operator, values)
        if (range !== undefined) params[`filter[${filter.columnId}]`] = range
        break
      }
      case 'option': {
        params[`filter[${filter.columnId}]`] = filter.values.join(',')
        break
      }
    }
  })

  return params
}
